import {
  Alert, Badge, Button, Card, Code, Drawer, Group, Loader, MultiSelect,
  NumberInput, Select, Stack, Switch, Table, Tabs, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle, IconMail, IconPlus, IconSend, IconUsers,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../shared/api/client";
import { PageHeader, EmptyState } from "../../../shared/ui";
import { confirmAction, confirmDelete } from "../../../shared/ui/confirm";

interface Campaign {
  id: string;
  name: string;
  subject: string;
  html: string;
  text: string;
  filters: Record<string, any>;
  status: "DRAFT" | "SENDING" | "SENT" | "FAILED" | "CANCELLED";
  created_by_username?: string;
  scheduled_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
}

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "gray", SENDING: "blue", SENT: "green",
  FAILED: "red", CANCELLED: "yellow",
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Taslak", SENDING: "Gönderiliyor", SENT: "Gönderildi",
  FAILED: "Başarısız", CANCELLED: "İptal",
};

const LOYALTY_TIER_OPTIONS = [
  { value: "BRONZE", label: "Bronze" },
  { value: "SILVER", label: "Silver" },
  { value: "GOLD", label: "Gold" },
  { value: "PLATINUM", label: "Platinum" },
];

export function CampaignsPage() {
  const qc = useQueryClient();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [detailOpen, setDetailOpen] = useState<string | null>(null);

  // Sadece SENDING/QUEUED kampanya varken 15 sn'de bir kontrol et — listenin
  // durağan olduğu zamanlarda (hepsi DRAFT/SENT/FAILED) polling'i 5 dk'ya
  // düşür. Yeni kampanya kullanıcı tarafından yaratıldığında mutation zaten
  // invalidate ediyor.
  const { data, isLoading, refetch } = useQuery<{ results?: Campaign[] } | Campaign[]>({
    queryKey: ["email-campaigns"],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/v1/email-campaigns/");
      return data;
    },
    refetchInterval: (query) => {
      const d = query.state.data as any;
      const list: Campaign[] = Array.isArray(d) ? d : (d?.results ?? []);
      const hasActive = list.some((c: any) =>
        ["SENDING", "QUEUED", "SCHEDULED"].includes(c.status),
      );
      return hasActive ? 15000 : 5 * 60 * 1000;
    },
  });
  const campaigns: Campaign[] = Array.isArray(data) ? data : (data?.results ?? []);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/email-campaigns/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-campaigns"] }),
  });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <PageHeader
          title="Toplu Mail Kampanyaları"
          subtitle={`${campaigns.length} kampanya · müşterilere e-posta gönderim`}
        />
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => { setEditing(null); setComposerOpen(true); }}
        >
          Yeni Kampanya
        </Button>
      </Group>

      {isLoading ? (
        <Loader />
      ) : campaigns.length === 0 ? (
        <EmptyState
          title="Henüz kampanya yok"
          description='İlk kampanyanızı oluşturmak için "Yeni Kampanya" tıklayın.'
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Konu</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Alıcı</Table.Th>
                <Table.Th>Gönderildi</Table.Th>
                <Table.Th>Hata</Table.Th>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>İşlem</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {campaigns.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td fw={600}>{c.name}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>{c.subject}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[c.status]} size="sm">
                      {STATUS_LABEL[c.status]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{c.recipient_count}</Table.Td>
                  <Table.Td>{c.sent_count}</Table.Td>
                  <Table.Td>
                    {c.failed_count > 0
                      ? <Text c="red" size="sm">{c.failed_count}</Text>
                      : <Text c="dimmed" size="sm">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {new Date(c.created_at).toLocaleDateString("tr-TR")}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setDetailOpen(c.id)}
                      >
                        Detay
                      </Button>
                      {c.status === "DRAFT" && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={async () => {
                            if (await confirmDelete(`"${c.name}" silinsin mi?`)) {
                              remove.mutate(c.id);
                            }
                          }}
                        >
                          Sil
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <CampaignComposer
        opened={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={() => { refetch(); setComposerOpen(false); }}
        editing={editing}
      />

      {detailOpen && (
        <CampaignDetail
          campaignId={detailOpen}
          onClose={() => setDetailOpen(null)}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Kampanya yazma drawer'ı
// ---------------------------------------------------------------------------
interface ComposerProps {
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: Campaign | null;
}

function CampaignComposer({ opened, onClose, onSaved, editing }: ComposerProps) {
  const [name, setName] = useState(editing?.name ?? "");
  const [subject, setSubject] = useState(editing?.subject ?? "");
  const [html, setHtml] = useState(
    editing?.html ?? "<p>Merhaba {{customer_name}},</p>\n<p>...</p>"
  );
  const [text, setText] = useState(editing?.text ?? "");

  // Filtreler
  const [optInRequired, setOptInRequired] = useState(
    editing?.filters?.opt_in_required ?? true
  );
  const [hasEmail, setHasEmail] = useState(
    editing?.filters?.has_email ?? true
  );
  const [loyaltyTiers, setLoyaltyTiers] = useState<string[]>(
    editing?.filters?.loyalty_tiers ?? []
  );
  const [minTotalMinor, setMinTotalMinor] = useState<number | "">(
    editing?.filters?.min_total_minor ?? ""
  );
  const [lastVisitWithin, setLastVisitWithin] = useState<number | "">(
    editing?.filters?.last_visit_within_days ?? ""
  );
  const [lastVisitOlder, setLastVisitOlder] = useState<number | "">(
    editing?.filters?.last_visit_older_than_days ?? ""
  );

  const filters = useMemo(() => {
    const f: Record<string, any> = {
      opt_in_required: optInRequired,
      has_email: hasEmail,
    };
    if (loyaltyTiers.length > 0) f.loyalty_tiers = loyaltyTiers;
    if (minTotalMinor !== "" && Number(minTotalMinor) > 0) f.min_total_minor = Number(minTotalMinor);
    if (lastVisitWithin !== "" && Number(lastVisitWithin) > 0)
      f.last_visit_within_days = Number(lastVisitWithin);
    if (lastVisitOlder !== "" && Number(lastVisitOlder) > 0)
      f.last_visit_older_than_days = Number(lastVisitOlder);
    return f;
  }, [optInRequired, hasEmail, loyaltyTiers, minTotalMinor, lastVisitWithin, lastVisitOlder]);

  // Segment preview
  const preview = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        "/api/v1/email-campaigns/preview-segment/",
        { filters },
      );
      return data as { total: number; sample: any[] };
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { name, subject, html, text, filters };
      if (editing) {
        const { data } = await apiClient.patch(
          `/api/v1/email-campaigns/${editing.id}/`, body,
        );
        return data;
      }
      const { data } = await apiClient.post("/api/v1/email-campaigns/", body);
      return data;
    },
    onSuccess: () => {
      notifications.show({ message: "Kampanya kaydedildi", color: "green" });
      onSaved();
    },
    onError: (err: any) => {
      notifications.show({
        message: err?.response?.data?.detail || "Kayıt başarısız",
        color: "red",
      });
    },
  });

  const [testEmail, setTestEmail] = useState("");
  const sendTest = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error("Önce kampanyayı kaydedin");
      const { data } = await apiClient.post(
        `/api/v1/email-campaigns/${editing.id}/send-test/`,
        { to_email: testEmail },
      );
      return data;
    },
    onSuccess: (data: any) => {
      notifications.show({
        message: data?.sent
          ? `Test maili gönderildi (${testEmail})`
          : `Test maili: ${data?.reason || "log"}`,
        color: data?.sent ? "green" : "yellow",
      });
    },
    onError: (err: any) => {
      notifications.show({
        message: err?.response?.data?.detail || err?.message || "Test başarısız",
        color: "red",
      });
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error("Önce kampanyayı kaydedin");
      const { data } = await apiClient.post(
        `/api/v1/email-campaigns/${editing.id}/send/`,
      );
      return data;
    },
    onSuccess: (data: any) => {
      const mode = data?.result?.mode;
      const total = data?.result?.total_recipients ?? 0;
      const msg =
        mode === "async"
          ? `${total} alıcı için ${data.result.queued_batches} batch sıraya alındı`
          : mode === "sync" || mode === "sync_fallback"
            ? `${total} kişiye senkron gönderildi`
            : `Hiç alıcı yoktu`;
      notifications.show({
        message: msg, color: "green", autoClose: 8000,
      });
      onSaved();
    },
    onError: (err: any) => {
      notifications.show({
        message: err?.response?.data?.detail || "Gönderim başarısız",
        color: "red",
      });
    },
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? "Kampanyayı Düzenle" : "Yeni Kampanya"}
      position="right"
      size="xl"
    >
      <Tabs defaultValue="content">
        <Tabs.List>
          <Tabs.Tab value="content">İçerik</Tabs.Tab>
          <Tabs.Tab value="segment">Hedef Kitle</Tabs.Tab>
          <Tabs.Tab value="send" disabled={!editing}>Gönderim</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="content" pt="md">
          <Stack gap="sm">
            <TextInput
              label="Kampanya adı (iç kullanım)"
              placeholder="Bahar İndirimleri 2026"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
            />
            <TextInput
              label="E-posta konusu"
              placeholder="🌸 Bahara özel %20 indirim sizleri bekliyor"
              value={subject}
              onChange={(e) => setSubject(e.currentTarget.value)}
              required
            />
            <Alert icon={<IconAlertCircle size={16} />} variant="light">
              Şablon değişkenleri: <Code>{"{{customer_name}}"}</Code>{" "}
              <Code>{"{{tenant_name}}"}</Code> <Code>{"{{email}}"}</Code>
            </Alert>
            <Textarea
              label="HTML içerik"
              placeholder="<h1>Merhaba {{customer_name}}!</h1>"
              minRows={10}
              autosize
              value={html}
              onChange={(e) => setHtml(e.currentTarget.value)}
              styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
            />
            <Textarea
              label="Düz metin alternatif (opsiyonel)"
              placeholder="HTML görmeyen istemciler için"
              minRows={3}
              autosize
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="segment" pt="md">
          <Stack gap="sm">
            <Switch
              label="Sadece marketing izni veren müşterilere (GDPR/KVKK)"
              checked={optInRequired}
              onChange={(e) => setOptInRequired(e.currentTarget.checked)}
            />
            <Switch
              label="Sadece e-postası olan müşteriler"
              checked={hasEmail}
              onChange={(e) => setHasEmail(e.currentTarget.checked)}
            />
            <MultiSelect
              label="Sadakat seviyesi (loyalty tier)"
              placeholder="Tüm seviyeler"
              data={LOYALTY_TIER_OPTIONS}
              value={loyaltyTiers}
              onChange={setLoyaltyTiers}
              clearable
            />
            <NumberInput
              label="Minimum toplam harcama (kuruş)"
              description="örn. 10000 = 100.00 TL"
              value={minTotalMinor}
              onChange={(v) => setMinTotalMinor(typeof v === "number" ? v : "")}
              min={0}
              step={1000}
            />
            <Group grow>
              <NumberInput
                label="Son N gün içinde ziyaret etmiş"
                placeholder="örn. 30"
                value={lastVisitWithin}
                onChange={(v) => setLastVisitWithin(typeof v === "number" ? v : "")}
                min={0}
              />
              <NumberInput
                label="Son ziyaret N günden daha eski"
                placeholder="örn. 90 (kaybedilenler)"
                value={lastVisitOlder}
                onChange={(v) => setLastVisitOlder(typeof v === "number" ? v : "")}
                min={0}
              />
            </Group>

            <Group justify="space-between" mt="sm">
              <Button
                leftSection={<IconUsers size={16} />}
                variant="light"
                onClick={() => preview.mutate()}
                loading={preview.isPending}
              >
                Hedef kitleyi say
              </Button>
              {preview.data && (
                <Group gap="xs">
                  <Badge size="lg" color="blue">
                    {preview.data.total} müşteri eşleşti
                  </Badge>
                </Group>
              )}
            </Group>
            {preview.data && preview.data.sample.length > 0 && (
              <Card withBorder padding="xs">
                <Text size="xs" c="dimmed" mb={4}>Örnek (ilk 10)</Text>
                <Stack gap={2}>
                  {preview.data.sample.map((s: any) => (
                    <Text key={s.id} size="xs">
                      {s.display_name || s.phone} — {s.email || "(email yok)"}
                    </Text>
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="send" pt="md">
          <Stack gap="sm">
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              Önce bir test maili gönderip görseli kontrol etmenizi öneririz.
              Toplu gönderim geri alınamaz.
            </Alert>
            <Group>
              <TextInput
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                variant="light"
                leftSection={<IconMail size={16} />}
                onClick={() => sendTest.mutate()}
                loading={sendTest.isPending}
                disabled={!testEmail || !testEmail.includes("@")}
              >
                Test maili gönder
              </Button>
            </Group>
            <Button
              size="md"
              color="red"
              leftSection={<IconSend size={18} />}
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Toplu e-posta gönderimi",
                  message: "Tüm hedef kitleye e-posta gönderilecek. Onaylıyor musunuz?",
                  confirmLabel: "Gönder",
                });
                if (ok) send.mutate();
              }}
              loading={send.isPending}
              disabled={!editing || editing.status !== "DRAFT"}
            >
              {editing?.status === "DRAFT" ? "Toplu Gönder" : `Durum: ${editing?.status}`}
            </Button>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onClose}>Kapat</Button>
        <Button
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!name || !subject || !html}
        >
          {editing ? "Güncelle" : "Taslak Olarak Kaydet"}
        </Button>
      </Group>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Detay drawer — gönderim sonrası alıcı listesi
// ---------------------------------------------------------------------------
function CampaignDetail({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const { data: campaign } = useQuery<Campaign>({
    queryKey: ["email-campaigns", campaignId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/api/v1/email-campaigns/${campaignId}/`);
      return data;
    },
  });
  const { data: recipientsData } = useQuery<{ count: number; results: any[] }>({
    queryKey: ["email-campaigns", campaignId, "recipients", statusFilter],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/api/v1/email-campaigns/${campaignId}/recipients/`,
        { params: statusFilter ? { status: statusFilter } : {} },
      );
      return data;
    },
  });

  if (!campaign) return null;
  const recipients = recipientsData?.results ?? [];

  return (
    <Drawer
      opened
      onClose={onClose}
      title={campaign.name}
      position="right"
      size="lg"
    >
      <Stack gap="md">
        <Card withBorder padding="sm">
          <Group justify="space-between">
            <Badge color={STATUS_COLOR[campaign.status]} size="lg">
              {STATUS_LABEL[campaign.status]}
            </Badge>
            <Text size="sm" c="dimmed">
              {campaign.completed_at
                ? `Tamamlandı: ${new Date(campaign.completed_at).toLocaleString("tr-TR")}`
                : campaign.started_at
                  ? `Başladı: ${new Date(campaign.started_at).toLocaleString("tr-TR")}`
                  : ""}
            </Text>
          </Group>
          <Group mt="sm" gap="xl">
            <Stat label="Alıcı" value={campaign.recipient_count} />
            <Stat label="Gönderildi" value={campaign.sent_count} color="green" />
            <Stat label="Hata" value={campaign.failed_count} color="red" />
          </Group>
        </Card>

        <div>
          <Title order={6} mb="xs">Konu</Title>
          <Text size="sm">{campaign.subject}</Text>
        </div>

        <div>
          <Group justify="space-between" mb="xs">
            <Title order={6}>Alıcılar ({recipientsData?.count ?? 0})</Title>
            <Select
              size="xs"
              placeholder="Tüm durumlar"
              data={[
                { value: "SENT", label: "Gönderildi" },
                { value: "FAILED", label: "Başarısız" },
                { value: "PENDING", label: "Bekliyor" },
                { value: "SKIPPED", label: "Atlandı" },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              w={180}
            />
          </Group>
          {recipients.length === 0 ? (
            <Text c="dimmed" size="sm">Kayıt yok</Text>
          ) : (
            <Card withBorder padding={0}>
              <Table.ScrollContainer minWidth={480}>
              <Table fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Müşteri</Table.Th>
                    <Table.Th>E-posta</Table.Th>
                    <Table.Th>Durum</Table.Th>
                    <Table.Th>Hata</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recipients.map((r: any) => (
                    <Table.Tr key={r.id}>
                      <Table.Td>{r.customer_name || r.customer_phone}</Table.Td>
                      <Table.Td>{r.email}</Table.Td>
                      <Table.Td>
                        <Badge
                          size="xs"
                          color={
                            r.status === "SENT" ? "green"
                              : r.status === "FAILED" ? "red"
                                : r.status === "SKIPPED" ? "yellow" : "gray"
                          }
                        >
                          {r.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="red" lineClamp={1}>
                          {r.error_message}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </div>
      </Stack>
    </Drawer>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="xl" fw={700} {...(color ? { c: color } : {})}>{value}</Text>
    </div>
  );
}
